import { FC, useEffect, useState } from 'react'
import { Select } from 'antd'
import type { FormInstance } from 'antd/es/form/Form'
import FormItem from 'antd/es/form/FormItem'
import { useWatch } from 'antd/es/form/Form'
import { CheckBoxLabel } from '@UI/CheckBox'
import TooltipWrapper from '@UI/New/TooltipWrapper/TooltipWrapper'
import { usersAPI } from '@/api'
import CreateLinkNumberInput, {
  createLinkNumberRuleTransform,
} from '../../CreateLinkNumberInput'
import { RequestCreateLeadPaymentLink } from '@/api/types/crm/crm.types'
import spclasses from '../../../SetCourseClassDirectionModal/purchase.module.css'
import sclasses from '../../../SetCourseClassDirectionModal/index.module.css'

type FieldType = Omit<RequestCreateLeadPaymentLink, 'lead_funnel_id'>

export type CourseLinkTeacherOption = {
  value: number
  label: string
  rate: number | null
}

type CourseLinkTeacherSelectProps = {
  form: FormInstance
  courseId?: number | null
}

const NO_TEACHERS_TOOLTIP =
  'В выбранном курсе пока нет педагогов со ставкой. Добавьте педагога в настройках курса.'

/**
 * Чекбокс «Привязать педагога» + селект + ЗП (salary_per_hour)
 * для CRM-ссылки hour / minute. По умолчанию выкл., как у групп.
 */
const CourseLinkTeacherSelect: FC<CourseLinkTeacherSelectProps> = ({
  form,
  courseId,
}) => {
  const [isAttachTeacher, setIsAttachTeacher] = useState(false)
  const [options, setOptions] = useState<CourseLinkTeacherOption[]>([])
  const [loading, setLoading] = useState(false)

  const teacherIdWatch = useWatch('teacher_id', form) as number | null

  const hasTeachers = options.length > 0
  const isCheckboxDisabled = !loading && !hasTeachers

  useEffect(() => {
    setIsAttachTeacher(false)
    form.setFieldsValue({
      teacher_id: null,
      salary_per_hour: null,
    })
  }, [courseId, form])

  useEffect(() => {
    if (!courseId) {
      setOptions([])
      return
    }

    let cancelled = false
    setLoading(true)
    usersAPI
      .getSeminarian({
        direction_id: courseId,
        expand: ['seminarian'],
        pageSize: false,
      })
      .then((res) => {
        if (cancelled) return
        const rows = (res?.data?.data ?? []) as Array<{
          seminarian_id?: number | null
          rate?: number | string | null
          seminarian?: { id?: number; name?: string | null } | null
        }>

        setOptions(
          rows
            .map((row) => {
              const teacherId = Number(
                row.seminarian_id ?? row.seminarian?.id ?? NaN,
              )
              if (!Number.isFinite(teacherId)) return null

              const rateRaw = Number(row.rate)
              return {
                value: teacherId,
                label: row.seminarian?.name || `Педагог #${teacherId}`,
                rate: Number.isFinite(rateRaw) ? rateRaw : null,
              }
            })
            .filter(Boolean) as CourseLinkTeacherOption[],
        )
      })
      .catch(() => {
        if (!cancelled) setOptions([])
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [courseId])

  useEffect(() => {
    if (isCheckboxDisabled && isAttachTeacher) {
      setIsAttachTeacher(false)
      form.setFieldsValue({
        teacher_id: null,
        salary_per_hour: null,
      })
    }
  }, [form, isAttachTeacher, isCheckboxDisabled])

  const onToggleAttach = (checked?: boolean) => {
    if (isCheckboxDisabled) return

    const nextChecked =
      typeof checked === 'boolean' ? checked : !isAttachTeacher
    setIsAttachTeacher(nextChecked)
    if (!nextChecked) {
      form.setFieldsValue({
        teacher_id: null,
        salary_per_hour: null,
      })
    }
  }

  const onTeacherChange = (teacherId: number | null) => {
    const selected = options.find((item) => item.value === teacherId)
    form.setFieldsValue({
      teacher_id: teacherId,
      salary_per_hour: selected?.rate ?? null,
    })
  }

  const checkbox = (
    <CheckBoxLabel
      type="button"
      value={isAttachTeacher}
      disabled={isCheckboxDisabled}
      onClick={onToggleAttach}
      label="Привязать педагога"
      className={spclasses.checkboxWithLabel}
      style={{
        justifyContent: 'flex-start',
        paddingTop: '4px',
        paddingBottom: '4px',
        height: 'auto',
      }}
    />
  )

  return (
    <div className={spclasses.inputWithLabel}>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        {isCheckboxDisabled ? (
          <TooltipWrapper placement="top" title={NO_TEACHERS_TOOLTIP}>
            <span style={{ display: 'inline-flex' }}>{checkbox}</span>
          </TooltipWrapper>
        ) : (
          checkbox
        )}
      </div>

      {isAttachTeacher && !isCheckboxDisabled ? (
        <div style={{ paddingTop: '4px' }}>
          <div className={spclasses.inputLabel}>Педагог</div>
          <FormItem<FieldType>
            name="teacher_id"
            rules={[
              {
                required: true,
                message: 'Выберите педагога или снимите чекбокс',
              },
            ]}
            noStyle
          >
            <Select
              showSearch
              loading={loading}
              placeholder="Выберите педагога"
              className={spclasses.select}
              optionFilterProp="label"
              options={options}
              onChange={onTeacherChange}
              allowClear
            />
          </FormItem>

          {teacherIdWatch != null ? (
            <div className={sclasses.inputWithLabel} style={{ marginTop: 8 }}>
              <FormItem<FieldType>
                name="salary_per_hour"
                rules={[
                  {
                    required: true,
                    message: 'Укажите ЗП педагога',
                  },
                  {
                    type: 'number',
                    min: 0,
                    message: 'ЗП не может быть отрицательной',
                    transform: createLinkNumberRuleTransform,
                  },
                ]}
                noStyle
              >
                <CreateLinkNumberInput
                  className={spclasses.input}
                  title="ЗП педагога за занятие (₽)"
                  min={0}
                  placeholder={0}
                />
              </FormItem>
            </div>
          ) : null}
        </div>
      ) : (
        <>
          <FormItem<FieldType> name="teacher_id" hidden>
            <input type="hidden" />
          </FormItem>
          <FormItem<FieldType> name="salary_per_hour" hidden>
            <input type="hidden" />
          </FormItem>
        </>
      )}
    </div>
  )
}

export default CourseLinkTeacherSelect
